/**
 * BM-H4437: QuickBooks invoice sync, as accounts sees it in Admin Tools.
 *
 * "Show codes" lists the QuickBooks items and VAT codes (GET
 * /api/qbo-invoice-sync/setup) so accounts can pick the ones the invoice
 * sync posts to; the chosen ids go into the env settings named beside them.
 * "Sync status" shows whether the sync is on, manual or automatic, what is
 * missing from its set-up, and any invoice that failed or is waiting for a
 * void (GET /api/qbo-invoice-sync/status). Both are read only.
 *
 * axios, not the page's fetch helper: these routes need the signed session
 * cookie, which the shared interceptor and withCredentials send.
 */
import React, { useState } from "react";
import axios from "axios";
import { Activity, ListChecks, Loader2, XCircle } from "lucide-react";
import { Button } from "./ui/button";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";

const btnClass =
  "min-w-[260px] justify-start gap-2 bg-navy-hover border border-navy-border text-txt hover:bg-teal hover:text-white transition-all disabled:opacity-40";

function CodeList({ title, rows, env }) {
  return (
    <div>
      <p className="text-xs font-semibold text-txt mb-1">{title}</p>
      {env && <p className="text-[11px] text-txt-muted mb-1">Goes in: {env}</p>}
      {rows.length === 0 ? (
        <p className="text-xs text-txt-muted">None found in QuickBooks.</p>
      ) : (
        <ul className="text-xs text-txt space-y-0.5" data-testid={`qbo-codes-${title.toLowerCase().replace(/\s+/g, "-")}`}>
          {rows.map((r) => (
            <li key={r.id}>
              <span className="font-mono text-teal">{r.id}</span> {r.name}
              {r.type ? <span className="text-txt-muted"> ({r.type})</span> : null}
              {r.description ? <span className="text-txt-muted"> {r.description}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function QboInvoiceSyncTile() {
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [codes, setCodes] = useState(null);
  const [status, setStatus] = useState(null);

  const load = async (which) => {
    setBusy(which);
    setError("");
    try {
      const url = which === "setup" ? `${API}/qbo-invoice-sync/setup` : `${API}/qbo-invoice-sync/status`;
      const resp = await axios.get(url);
      if (which === "setup") setCodes(resp.data);
      else setStatus(resp.data);
    } catch (err) {
      setError(errorMessage(err, "Could not read QuickBooks"));
    } finally {
      setBusy("");
    }
  };

  const env = codes?.env_to_set || {};
  const mode = !status ? "" : !status.live ? "Switched off" : status.auto ? "On, automatic" : "On, manual";

  return (
    <div className="space-y-3" data-testid="qbo-invoice-sync-tile">
      <div className="flex flex-wrap gap-3">
        <Button onClick={() => load("setup")} disabled={!!busy} className={btnClass} data-testid="qbo-show-codes">
          {busy === "setup" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ListChecks className="w-4 h-4" />}
          Show QuickBooks item and VAT codes
        </Button>
        <Button onClick={() => load("status")} disabled={!!busy} className={btnClass} data-testid="qbo-sync-status">
          {busy === "status" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
          Invoice sync status
        </Button>
      </div>

      {error && (
        <p className="text-sm text-status-red flex items-center gap-1" data-testid="qbo-tile-error">
          <XCircle className="w-4 h-4" /> {error}
        </p>
      )}

      {status && (
        <div className="text-xs text-txt space-y-1" data-testid="qbo-status-panel">
          <p><span className="font-semibold">Invoice sync:</span> <span data-testid="qbo-status-mode">{mode}</span></p>
          {status.missing_config?.length > 0 && (
            <p className="text-txt-muted">Still to set up: {status.missing_config.join(", ")}</p>
          )}
          <p className="text-txt-muted">
            Sent {status.counts?.pushed ?? 0} · Failed {status.counts?.failed ?? 0} · Totals differ {status.counts?.mismatch ?? 0} · Waiting {status.counts?.queued ?? 0}
          </p>
          {status.needs_attention?.map((inv) => (
            <p key={inv.id} className="text-status-red">
              Invoice {inv.invoice_number} ({inv.matter_ref}): {inv.qbo_sync?.error || inv.qbo_sync?.state}
            </p>
          ))}
          {status.void_waiting?.map((inv) => (
            <p key={inv.id}>Invoice {inv.invoice_number} ({inv.matter_ref}) is void here but still live in QuickBooks.</p>
          ))}
        </div>
      )}

      {codes && (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="qbo-codes-panel">
          <CodeList
            title="Items"
            rows={codes.items || []}
            env={[env.fees_item, env.disbursements_item, env.other_fees_item].filter(Boolean).join(", ")}
          />
          <CodeList
            title="VAT codes"
            rows={codes.tax_codes || []}
            env={[env.vat_code, env.no_vat_code].filter(Boolean).join(", ")}
          />
        </div>
      )}
    </div>
  );
}
